import { useState, type ReactNode } from "react"
import { ChevronLeft, ShieldCheck, Check } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "../Button/Button"
import { Separator } from "../Separator/Separator"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../Select/Select"

export type CheckoutFormVariant = "page" | "modal"

export interface CheckoutSummaryRow {
  label: string
  value: ReactNode
}

export interface CheckoutOrder {
  pageTitle?: string
  summaryTitle: string
  summaryRows: CheckoutSummaryRow[]
  dueTodayLabel: string
  dueTodayPrice: ReactNode
  nextBilling?: ReactNode
  benefits?: string[]
  disclaimer?: ReactNode
  ctaLabel: string
  footer?: ReactNode
}

export interface CheckoutFormProps {
  order: CheckoutOrder
  variant?: CheckoutFormVariant
  processing?: boolean
  /**
   * Real payment UI, rendered in place of the built-in card fields.
   *
   * The fields below are a layout mock — they collect nothing and submit
   * nothing. Anything charging an actual card passes a slot (a Stripe
   * PaymentElement, a saved-method picker) and keeps the surrounding chrome:
   * order summary, disclaimer, CTA and the secure-payment footer.
   */
  paymentSlot?: ReactNode
  orderSummaryHeading?: string
  paymentHeading?: string
  secureCaption?: string
  secureFooter?: ReactNode
  cardNumberLabel?: string
  cardNumberPlaceholder?: string
  expiryLabel?: string
  expiryPlaceholder?: string
  cvcLabel?: string
  cvcPlaceholder?: string
  countryLabel?: string
  /** Only used by the built-in card fields; ignored when paymentSlot is set. */
  countries?: string[]
  defaultCountry?: string
  country?: string
  onCountryChange?: (value: string) => void
  processingLabel?: string
  onSubmit?: () => void
  onBack?: () => void
  className?: string
}

export function CheckoutForm({
  order,
  variant = "page",
  processing = false,
  paymentSlot,
  orderSummaryHeading = "Order Summary",
  paymentHeading = "Payment",
  secureCaption = "Secure checkout. Your card is never stored.",
  secureFooter = "Your payment information is secure and encrypted.",
  cardNumberLabel = "Card number",
  cardNumberPlaceholder = "1234 1234 1234 1234",
  expiryLabel = "Expiration date",
  expiryPlaceholder = "MM / YY",
  cvcLabel = "Security code",
  cvcPlaceholder = "CVC",
  countryLabel = "Country",
  countries = [],
  defaultCountry,
  country,
  onCountryChange,
  processingLabel = "Processing...",
  onSubmit,
  onBack,
  className,
}: CheckoutFormProps) {
  const [internalCountry, setInternalCountry] = useState(
    defaultCountry ?? countries[0] ?? "",
  )
  const isCountryControlled = country !== undefined
  const currentCountry = isCountryControlled ? country : internalCountry

  const handleCountryChange = (next: string) => {
    if (!isCountryControlled) setInternalCountry(next)
    onCountryChange?.(next)
  }

  const isModal = variant === "modal"

  return (
    <div className={cn(isModal ? "" : "max-w-3xl", className)}>
      {!isModal && order.pageTitle && (
        <div className="flex items-center gap-3 mb-6">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              aria-label="Back"
              className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            >
              <ChevronLeft size={20} strokeWidth={1.5} />
            </button>
          )}
          <h1 className="text-page-title text-foreground">{order.pageTitle}</h1>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h2 className="text-section text-foreground mb-4">{orderSummaryHeading}</h2>
          <div className="bg-card border border-border rounded-md p-5">
            <h3 className="text-section text-foreground font-semibold mb-3">
              {order.summaryTitle}
            </h3>
            <div className="flex flex-col gap-2 text-body">
              {order.summaryRows.map((row, i) => (
                <div key={i} className="flex justify-between">
                  <span className="text-muted-foreground">{row.label}</span>
                  <span className="text-foreground">{row.value}</span>
                </div>
              ))}
            </div>
            <Separator className="my-4" />
            <div className="flex justify-between text-body font-semibold">
              <span className="text-foreground">{order.dueTodayLabel}</span>
              <span className="text-primary text-section">{order.dueTodayPrice}</span>
            </div>
            {order.nextBilling && (
              <div className="flex justify-between text-caption text-muted-foreground mt-1">
                <span>Next billing:</span>
                <span>{order.nextBilling}</span>
              </div>
            )}
            {order.benefits && order.benefits.length > 0 && (
              <div className="mt-4 flex flex-col gap-1.5">
                {order.benefits.map((benefit) => (
                  <div
                    key={benefit}
                    className="flex items-center gap-2 text-caption text-foreground"
                  >
                    <Check size={12} className="text-muted-foreground" /> {benefit}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div>
          <h2 className="text-section text-foreground mb-4">{paymentHeading}</h2>
          <div className="bg-card border border-border rounded-md p-5">
            <p className="text-caption text-muted-foreground mb-4">{secureCaption}</p>
            {paymentSlot ?? (
              <div className="flex flex-col gap-3">
                <div>
                  <label className="text-caption text-muted-foreground mb-1 block">
                    {cardNumberLabel}
                  </label>
                  <input
                    placeholder={cardNumberPlaceholder}
                    className="w-full bg-muted border border-border rounded-md px-3 py-2 text-body text-foreground outline-none placeholder:text-muted-foreground/50 focus:border-primary transition-colors"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-caption text-muted-foreground mb-1 block">
                      {expiryLabel}
                    </label>
                    <input
                      placeholder={expiryPlaceholder}
                      className="w-full bg-muted border border-border rounded-md px-3 py-2 text-body text-foreground outline-none placeholder:text-muted-foreground/50 focus:border-primary transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-caption text-muted-foreground mb-1 block">
                      {cvcLabel}
                    </label>
                    <input
                      placeholder={cvcPlaceholder}
                      className="w-full bg-muted border border-border rounded-md px-3 py-2 text-body text-foreground outline-none placeholder:text-muted-foreground/50 focus:border-primary transition-colors"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-caption text-muted-foreground mb-1 block">
                    {countryLabel}
                  </label>
                  <Select value={currentCountry} onValueChange={handleCountryChange}>
                    <SelectTrigger className="w-full bg-muted border-border text-body">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map((c) => (
                        <SelectItem key={c} value={c} className="text-body">
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            {order.disclaimer && (
              <p className="text-caption text-muted-foreground mt-3 leading-relaxed">
                {order.disclaimer}
              </p>
            )}
            <Button
              className="w-full mt-4 text-label"
              onClick={onSubmit}
              disabled={processing}
            >
              {processing ? processingLabel : order.ctaLabel}
            </Button>
            {order.footer && (
              <p className="text-caption text-muted-foreground mt-3">{order.footer}</p>
            )}
          </div>
          {secureFooter && (
            <p className="text-caption text-muted-foreground mt-3 font-medium flex items-center gap-1.5">
              <ShieldCheck size={13} strokeWidth={1.5} className="shrink-0" />
              {secureFooter}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
